import { logger } from '../../services/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Plus, Trash2, Image as ImageIcon, Loader2, HelpCircle, Zap } from 'lucide-react';
import { MarketCategory, DbMarketType } from '../../types';
import { supabase } from '../../lib/db';
import { preserveLocalDateTime } from '../../utils/dateUtils';
import { validateMarketCardImage } from '../../utils/imageUtils';
import { Category as CategoryType, fetchMarketCategories } from '../../services/categoryService';
import { useAuth } from '../../lib/auth';
import { toast } from 'react-hot-toast';
import { MarketStandardsAgreementModal } from '../MarketStandardsAgreementModal.tsx';
import { submitTradingMarket } from '../../lib/api.ts';

// Removed unused marketEngine variable

// Interface for form option state
interface CreateFormOption {
  // Use temporary IDs for keys, they won't be sent to backend
  tempId: number;
  name: string;
  trading_end_time?: string | null; // ISO string or null
  /** Pending file to upload on submit; cleared after successful upload path uses URL in payload */
  iconFile?: File | null;
  iconPreviewUrl?: string | null;
}

// Removed unused CreateMarketPayload interface

// Helper to format date for datetime-local input
const formatDateTimeLocal = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - timezoneOffset);
    return localDate.toISOString().slice(0, 16);
  } catch { return ''; }
};

export function CreateMarketForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminUser =
    user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
  const [title, setTitle] = useState('');
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [category, setCategory] = useState<MarketCategory | ''>('');
  const [marketType, setMarketType] = useState<DbMarketType>('fixed');
  const [options, setOptions] = useState<CreateFormOption[]>([
    { tempId: Math.random(), name: '', trading_end_time: null }, 
    { tempId: Math.random(), name: '', trading_end_time: null }
  ]);
  const [resolutionTime, setResolutionTime] = useState<string | null>(null);
  /** No scheduled resolution yet (stored as NULL; UI shows TBA until set in admin/editor). */
  const [resolutionOpenEnded, setResolutionOpenEnded] = useState(false);
  const [rules, setRules] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lmsrTier, setLmsrTier] = useState<'standard' | 'enhanced' | 'premium'>('standard');

  const LMSR_TIERS: Record<'standard' | 'enhanced' | 'premium', { b: number; label: string; description: string }> = {
    standard: { b: 500, label: 'Standard', description: 'Entry depth — wider price movement' },
    enhanced: { b: 1500, label: 'Enhanced', description: 'Balanced depth and stability' },
    premium: { b: 5000, label: 'Premium', description: 'Deepest book — prices move more gradually' },
  };

  const lmsrCreatorDeposit = (): number => {
    const b = LMSR_TIERS[lmsrTier].b;
    const n = Math.max(options.length, 2);
    return parseFloat((b * Math.log(n)).toFixed(2));
  };
  const [hasAgreedToStandards, setHasAgreedToStandards] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [currentStandardsVersion, setCurrentStandardsVersion] = useState<number | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchMarketCategories();
      setCategories(fetchedCategories);
      // Optionally set a default category if desired, e.g.:
      // if (fetchedCategories.length > 0) {
      //   setCategory(fetchedCategories[0].name as MarketCategory);
      // }
    };
    loadCategories();
  }, []);

  // Check if user has agreed to market standards
  useEffect(() => {
    const checkAgreement = async () => {
      if (!user) {
        setHasAgreedToStandards(false);
        return;
      }

      try {
        // First, get the current version of market standards
        const { data: standardsData, error: standardsError } = await supabase
          .rpc('get_active_site_content', { p_content_type: 'market_standards' });

        if (standardsError) {
          logger.error('Error fetching market standards:', standardsError);
          setHasAgreedToStandards(false);
          return;
        }

        if (!standardsData || standardsData.length === 0) {
          // No standards exist yet, allow creation
          setHasAgreedToStandards(true);
          setCurrentStandardsVersion(null);
          return;
        }

        const currentVersion = standardsData[0].version;
        setCurrentStandardsVersion(currentVersion);

        // Check if user has agreed to this specific version
        const { data: agreementData, error: agreementError } = await supabase
          .from('user_market_standards_agreement')
          .select('version, agreed_at')
          .eq('user_id', user.id)
          .eq('version', currentVersion)
          .maybeSingle(); // Use maybeSingle instead of single to avoid errors when no record exists

        if (agreementError) {
          // If it's a 403, it's a permissions issue - log but don't block
          if (agreementError.code === 'PGRST301' || agreementError.message?.includes('403')) {
            logger.error('Permission error checking agreement - RLS policy issue:', agreementError);
            // Allow creation but log the issue for admin review
            setHasAgreedToStandards(true);
            return;
          }
          logger.error('Error checking agreement:', agreementError);
          setHasAgreedToStandards(false);
          return;
        }

        // If data exists AND version matches current version, user has agreed
        const hasAgreed = !!agreementData && agreementData.version === currentVersion;
        setHasAgreedToStandards(hasAgreed);
        
        // Show modal ONLY if user hasn't agreed to the current version
        if (!hasAgreed) {
          setShowAgreementModal(true);
        } else {
          setShowAgreementModal(false);
        }
      } catch (err) {
        logger.error('Error checking agreement:', err);
        setHasAgreedToStandards(false);
        setShowAgreementModal(true);
      }
    };

    checkAgreement();
  }, [user]);

  // Handle agreement
  const handleAgree = async () => {
    if (!user) {
      toast.error('You must be logged in to create a market');
      return;
    }

    if (currentStandardsVersion === null) {
      toast.error('Unable to determine market standards version');
      return;
    }

    try {
      // Use upsert to handle case where agreement already exists
      // For composite primary keys, we need to specify both columns
      const { error } = await supabase
        .from('user_market_standards_agreement')
        .upsert({
          user_id: user.id,
          version: currentStandardsVersion,
          agreed_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,version',
          ignoreDuplicates: false // Update if exists
        });

      if (error) {
        // If it's a 403, it's a permissions issue
        if (error.code === 'PGRST301' || error.message?.includes('403')) {
          logger.error('Permission error saving agreement - RLS policy issue:', error);
          toast.error('Permission error. Please contact support if this persists.');
          return;
        }
        throw error;
      }

      setHasAgreedToStandards(true);
      setShowAgreementModal(false);
      toast.success('Thank you for agreeing to the market standards');
      
      // Re-check agreement to ensure state is correct
      const { data: verifyData } = await supabase
        .from('user_market_standards_agreement')
        .select('version')
        .eq('user_id', user.id)
        .eq('version', currentStandardsVersion)
        .maybeSingle();
      
      if (verifyData) {
        setHasAgreedToStandards(true);
      }
    } catch (err) {
      logger.error('Error saving agreement:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save agreement';
      toast.error(`Failed to save agreement: ${errorMessage}`);
    }
  };

  // Handler for option changes
  const handleOptionChange = (tempId: number, field: 'name' | 'trading_end_time', value: string) => {
    setOptions(prevOptions => 
      prevOptions.map(opt => {
        if (opt.tempId === tempId) {
          if (field === 'name') {
            return { ...opt, name: value };
          } else if (field === 'trading_end_time') {
            // Convert local datetime string back to ISO string or null
            const isoValue = preserveLocalDateTime(value);
            return { ...opt, trading_end_time: isoValue };
          }
        }
        return opt;
      })
    );
    // No inline errors to clear here, relying on toast
  };

  // Add a new option
  const addOption = () => {
    setOptions([...options, { tempId: Math.random(), name: '', trading_end_time: null }]);
  };

  // Remove an option
  const removeOption = (tempId: number) => {
    if (options.length <= 2) {
      toast.error('Market must have at least two options.');
      return;
    }
    setOptions((prev) =>
      prev
        .filter((opt) => {
          if (opt.tempId === tempId && opt.iconPreviewUrl) {
            URL.revokeObjectURL(opt.iconPreviewUrl);
          }
          return opt.tempId !== tempId;
        }),
    );
  };

  const clearOptionIcon = (tempId: number) => {
    setOptions((prev) =>
      prev.map((opt) => {
        if (opt.tempId !== tempId) return opt;
        if (opt.iconPreviewUrl) URL.revokeObjectURL(opt.iconPreviewUrl);
        return { ...opt, iconFile: null, iconPreviewUrl: null };
      }),
    );
    const input = document.getElementById(`option-icon-${tempId}`) as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const handleOptionIconChange = (tempId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Option icon must be an image file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Option icon must be under 2MB.');
      return;
    }
    setOptions((prev) =>
      prev.map((opt) => {
        if (opt.tempId !== tempId) return opt;
        if (opt.iconPreviewUrl) URL.revokeObjectURL(opt.iconPreviewUrl);
        return {
          ...opt,
          iconFile: file,
          iconPreviewUrl: URL.createObjectURL(file),
        };
      }),
    );
  };

  // Handle image upload
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic validation
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image size must be less than 5MB.');
      return;
    }

    // Validate image dimensions
    const validation = await validateMarketCardImage(file);
    if (!validation.isValid) {
      // Show warnings but don't block upload
      validation.warnings.forEach(warning => toast(warning, { duration: 4000 }));
      if (validation.recommendations.length > 0) {
        toast(`Tip: ${validation.recommendations[0]}`, { duration: 3000 });
      }
    }

    setCoverImage(file);
    setImagePreview(URL.createObjectURL(file));
  };


  // Add uploadImage function
  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `market-covers/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('market-images')
      .upload(filePath, file);

    if (uploadError) {
      throw new Error('Failed to upload image');
    }

    const { data: { publicUrl } } = supabase.storage
      .from('market-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const uploadOptionIcon = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop() || 'png';
    const filePath = `market-covers/option-icons/${crypto.randomUUID()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from('market-images').upload(filePath, file);
    if (uploadError) {
      throw new Error('Failed to upload option icon');
    }
    const {
      data: { publicUrl },
    } = supabase.storage.from('market-images').getPublicUrl(filePath);
    return publicUrl;
  };

  // Handle datetime-local input changes
  const handleDateTimeChange = (setter: React.Dispatch<React.SetStateAction<string | null>>, value: string) => {
    setter(preserveLocalDateTime(value));
    // No inline errors to clear here, relying on toast
  };
  
  // --- Validation Function ---
  const validateForm = (): boolean => {
      if (!title.trim()) { toast.error('Market Title is required.'); return false; }
      if (!category) { toast.error('Category is required.'); return false; }
      if (!resolutionOpenEnded && !resolutionTime) {
        toast.error('Pick a resolution date/time, or check \"resolution not set yet\" (TBA).');
        return false;
      }
      if (!coverImage) { toast.error('Cover image is required.'); return false; }
      if (options.length < 2) { toast.error('At least two market options are required.'); return false; }
      if (options.some(opt => !opt.name.trim())) { toast.error('All market option names must be filled.'); return false; }
      
      if (!resolutionOpenEnded && resolutionTime) {
        const now = new Date();
        const resTime = new Date(resolutionTime);
        if (resTime <= now) {
          toast.error('Resolution time must be in the future.');
          return false;
        }
      }

      return true;
  };

  // --- Form Submission ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user has agreed to standards
    if (!hasAgreedToStandards) {
      setShowAgreementModal(true);
      toast.error('You must agree to the market standards before creating a market');
      return;
    }
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // 1. Upload Images
      if (!coverImage) {
        toast.error("Cover image is missing.");
        setIsSubmitting(false);
        return;
      }

      const imageUrl = await uploadImage(coverImage);

      // 2. Upload per-option icons, then build RPC payload
      const finalOptionsPayload: {
        name: string;
        trading_end_time: string | null;
        image_url: string | null;
      }[] = [];
      for (const opt of options) {
        let optionImageUrl: string | null = null;
        if (opt.iconFile) {
          optionImageUrl = await uploadOptionIcon(opt.iconFile);
        }
        finalOptionsPayload.push({
          name: opt.name.trim(),
          trading_end_time: null,
          image_url: optionImageUrl,
        });
      }

      if (isAdminUser) {
        const payload = {
          p_title: title.trim(),
          p_description: rules.trim(),
          p_category: category,
          p_market_type: marketType,
          p_is_private: false,
          p_is_dynamic: true,
          p_fixed_fee: null,
          p_trading_end_time: null,
          p_resolution_time: resolutionOpenEnded ? null : resolutionTime,
          p_initial_options: finalOptionsPayload,
          p_status: 'active',
          p_image_url: imageUrl,
          p_featured_image_url: null,
          p_is_featured: false,
          p_engine_type: 'lmsr',
          p_lmsr_b: LMSR_TIERS[lmsrTier].b,
        };
        logger.info('Calling create_market_v2 RPC with payload:', payload);
        const { data: newMarketId, error: rpcError } = await supabase.rpc('create_market_v2', payload);
        if (rpcError) {
          logger.error('RPC create_market_v2 error details:', rpcError);
          throw rpcError;
        }
        const createdId = Array.isArray(newMarketId) ? newMarketId[0] : newMarketId;
        if (!createdId) {
          throw new Error('Market creation RPC did not return a valid market ID.');
        }
        toast.success('Market created successfully!');
        navigate(`/admin/market/${createdId}`);
      } else {
        const data = await submitTradingMarket({
          title: title.trim(),
          description: rules.trim(),
          category: category as string,
          market_type: marketType,
          is_private: false,
          is_dynamic: true,
          resolution_time: resolutionOpenEnded ? null : resolutionTime ?? null,
          trading_end_time: null,
          initial_options: finalOptionsPayload,
          image_url: imageUrl,
          featured_image_url: null,
          is_featured: false,
        });
        if (!data?.market_id) {
          throw new Error('Market submission did not return a market id.');
        }
        toast.success('Market submitted for approval.');
        navigate('/account/creator');
      }

    } catch (error: unknown) {
      logger.error('Error during market creation process:', error);
      const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
      let displayError = `Failed to create market: ${message}`;
      if (message.includes("upload")) displayError = `Failed to upload image: ${message}`;
      if (message.includes("RPC")) displayError = `Market creation failed: ${message}`;
      
      toast.error(displayError);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ensure setMarketType is used in the radio button onChange handlers
  const handleMarketTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setMarketType(event.target.value as DbMarketType);
  };
  
  // --- JSX ---
  return (
    <>
      <MarketStandardsAgreementModal
        isOpen={showAgreementModal}
        onAgree={handleAgree}
      />
      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto pb-24 md:pb-0">
        <h1 className="text-3xl font-bold mb-8 text-gray-900">Create New Market</h1>

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Market Title *</label>
         <div className="relative">
             <HelpCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
             <input
               type="text" id="title" value={title}
               onChange={(e) => setTitle(e.target.value)}
               placeholder="e.g., Who will win the next presidential election?"
               className="pl-10 w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
               required
             />
         </div>
      </div>

      {/* Category */}
      <div>
         <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
         <div className="relative">
           <select
             id="category" value={category}
             onChange={(e) => setCategory(e.target.value as MarketCategory)}
             className="pl-4 pr-10 w-full rounded-md border border-gray-300 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
             disabled={categories.length === 0} required
           >
             <option value="" disabled>Select Category...</option>
             {categories.map((cat) => (
               <option key={cat.id} value={cat.name}>{cat.name}</option>
             ))}
           </select>
         </div>
       </div>

       {/* Cover Image */}
       <div>
          <label htmlFor="cover-image-input" className="block text-sm font-medium text-gray-700 mb-1">Cover Image *</label>
          <div
            className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md ${imagePreview ? 'relative' : ''}`}
          >
            {imagePreview ? (
              <div className="relative w-full max-w-md">
                  <img src={imagePreview} alt="Preview" className="mx-auto h-32 w-auto object-cover rounded-lg" />
                    <button
                        type="button"
                        onClick={() => {
                            setImagePreview(null);
                            setCoverImage(null);
                            const input = document.getElementById('cover-image-input') as HTMLInputElement;
                            if (input) input.value = '';
                        }}
                        className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        aria-label="Remove image"
                     >
                         <Trash2 className="w-4 h-4" />
                    </button>
              </div>
            ) : (
              <div className="space-y-1 text-center">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400" strokeWidth={1}/>
                <div className="flex text-sm text-gray-600 justify-center">
                  <label
                    htmlFor="cover-image-input"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-wayja-teal hover:text-wayja-dark-teal focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-wayja-teal"
                  >
                    <span>Upload a file</span>
                    <input id="cover-image-input" name="cover-image" type="file" className="sr-only" onChange={handleImageChange} accept="image/*" required />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
              </div>
            )}
          </div>
        </div>

      {isAdminUser && (
        <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
          <label
            className="block text-sm font-semibold text-gray-800 flex items-center gap-2"
            title="Higher b means deeper liquidity: prices move more slowly as people trade."
          >
            <Zap className="w-4 h-4 text-wayja-teal" />
            Liquidity depth (b = {LMSR_TIERS[lmsrTier].b})
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(Object.keys(LMSR_TIERS) as Array<'standard' | 'enhanced' | 'premium'>).map((tier) => (
              <label key={tier} className={`border rounded-lg p-3 cursor-pointer transition-colors ${lmsrTier === tier ? 'border-wayja-teal bg-wayja-teal/5 ring-1 ring-wayja-teal' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name="lmsrTier" value={tier} checked={lmsrTier === tier} onChange={() => setLmsrTier(tier)} className="sr-only" />
                <p className="text-sm font-semibold text-gray-900">{LMSR_TIERS[tier].label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{LMSR_TIERS[tier].description}</p>
              </label>
            ))}
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
            <p className="font-medium text-amber-800">Liquidity depth (Wayja-funded)</p>
            <p className="text-amber-700 mt-1">
              R{lmsrCreatorDeposit().toFixed(2)} backing reference for this tier and {Math.max(options.length, 2)}{' '}
              outcomes (b × ln(N)). Wayja provides liquidity; creating here does not charge your personal wallet.
            </p>
          </div>
        </div>
      )}

    {/* Market Structure & Options */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Market Type *</label>
      <div className="flex flex-col sm:flex-row gap-4">
        <label className="flex-1 flex items-center gap-2 cursor-pointer p-4 border rounded-lg hover:bg-gray-50 transition-colors">
          <input
            type="radio" name="market_type" value="fixed"
            checked={marketType === 'fixed'} onChange={handleMarketTypeChange}
            className="form-radio h-4 w-4 text-wayja-teal focus:ring-wayja-teal border-gray-300 mt-1"
            required
          />
          <div className="ml-1">
            <span className="block text-sm font-medium text-gray-900">Standard Market</span>
            <span className="block text-xs text-gray-500 mt-1">Trading continues until resolution.</span>
          </div>
        </label>
        <label className="flex-1 flex items-center gap-2 cursor-pointer p-4 border rounded-lg hover:bg-gray-50 transition-colors">
          <input
            type="radio" name="market_type" value="knockout"
            checked={marketType === 'knockout'} onChange={handleMarketTypeChange}
            className="form-radio h-4 w-4 text-wayja-teal focus:ring-wayja-teal border-gray-300 mt-1"
          />
           <div className="ml-1">
            <span className="block text-sm font-medium text-gray-900">Knockout Market</span>
            <span className="block text-xs text-gray-500 mt-1">Options can be eliminated over time until resolution.</span>
          </div>
        </label>
      </div>
    </div>

    {/* Options Input */}
    <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">Market Options * <span className="text-xs text-gray-500">(Minimum 2)</span></label>
       <div className="space-y-3">
         {options.map((option, index) => (
             <div key={option.tempId} className="flex flex-col sm:flex-row items-start gap-2">
                <div className="flex-grow w-full flex flex-col sm:flex-row gap-2 sm:items-center min-w-0">
                   <div className="flex-1 min-w-0">
                     <label htmlFor={`option-name-${option.tempId}`} className="sr-only">Option Name {index + 1}</label>
                     <input
                       type="text" id={`option-name-${option.tempId}`} value={option.name}
                       onChange={(e) => handleOptionChange(option.tempId, 'name', e.target.value)}
                       placeholder={`Option ${index + 1} Name`}
                       className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                       required
                     />
                   </div>
                   <div className="flex items-center gap-2 shrink-0">
                     {option.iconPreviewUrl ? (
                       <img
                         src={option.iconPreviewUrl}
                         alt=""
                         className="w-8 h-8 rounded-full object-cover border border-gray-200"
                       />
                     ) : null}
                     <label
                       htmlFor={`option-icon-${option.tempId}`}
                       className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                     >
                       <ImageIcon className="w-3.5 h-3.5 text-gray-500" />
                       Icon
                       <input
                         id={`option-icon-${option.tempId}`}
                         type="file"
                         className="sr-only"
                         accept="image/*"
                         onChange={(e) => handleOptionIconChange(option.tempId, e)}
                       />
                     </label>
                     {option.iconPreviewUrl ? (
                       <button
                         type="button"
                         onClick={() => clearOptionIcon(option.tempId)}
                         className="text-xs text-gray-500 hover:text-red-600"
                       >
                         Clear
                       </button>
                     ) : null}
                   </div>
                </div>
                {/* Remove Button Style Adjustment */}
                {options.length > 2 && (
                  <button
                    type="button" onClick={() => removeOption(option.tempId)}
                    className="p-2 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed self-center sm:self-auto flex-shrink-0"
                    aria-label="Remove option"
                    disabled={options.length <= 2}
                  >
                      <Trash2 className="w-5 h-5" />
                  </button>
                )}
            </div>
        ))}
        <button
          type="button" onClick={addOption}
          className="mt-2 flex items-center gap-2 text-sm text-wayja-teal hover:text-wayja-dark-teal"
        >
          <Plus className="w-4 h-4" />
          Add Option
        </button>
      </div>
   </div>

    {/* Resolution Time (optional — TBA supported) */}
    <div className="space-y-3">
      <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-gray-200 bg-gray-50/80 p-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-wayja-teal focus:ring-wayja-teal"
          checked={resolutionOpenEnded}
          onChange={(e) => {
            const on = e.target.checked;
            setResolutionOpenEnded(on);
            if (on) setResolutionTime(null);
          }}
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">Resolution time not set yet (TBA)</span>
          <span className="block text-xs text-gray-500 mt-0.5">
            Use when there is no fixed deadline yet. Markets show “TBA” until you or an admin adds a resolution time.
          </span>
        </span>
      </label>

      <div className={`space-y-1 ${resolutionOpenEnded ? 'opacity-50 pointer-events-none' : ''}`}>
      <label htmlFor="resolution_time" className="block text-sm font-medium text-gray-700 mb-1">
        Resolution time {!resolutionOpenEnded ? '*' : '(optional)'}
      </label>
      <div className="relative">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="datetime-local" id="resolution_time"
            value={formatDateTimeLocal(resolutionTime)}
            onChange={(e) => handleDateTimeChange(setResolutionTime, e.target.value)}
            className="w-full pl-10 rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
            required={!resolutionOpenEnded}
            disabled={resolutionOpenEnded}
          />
      </div>
      <p className="text-xs text-gray-500 mt-1">When the final outcome is known (if you already have a date).</p>
      </div>
    </div>

    {/* Description/Rules */}
    <div>
      <label htmlFor="rules" className="block text-sm font-medium text-gray-700 mb-1">Detailed Description / Resolution Rules *</label>
       <textarea
         id="rules" rows={5} value={rules}
         onChange={(e) => setRules(e.target.value)}
         className="w-full rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
         placeholder="Clearly define the market question, how the outcome will be determined (source of truth), and any specific conditions..."
         required
       />
       <p className="text-xs text-gray-500 mt-1">Be specific. This helps prevent disputes.</p>
    </div>

    {/* Submission Area */}
    <div className="mt-8 flex justify-end items-center gap-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="flex-1 md:flex-none px-6 py-2 bg-wayja-teal text-white rounded-md hover:bg-wayja-dark-teal disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {isSubmitting && <Loader2 className="w-5 h-5 animate-spin" />}
        {isSubmitting
          ? 'Working...'
          : isAdminUser
            ? 'Create Market'
            : 'Submit for approval'}
      </button>
    </div>
  </form>
  </>
);
} 